import React, { useState, useEffect } from "react";
import { redirectToOAuth } from "../../../utils/safeUrl";
import axios from "axios";
import EmailBodyInput from './EmailBodyInput';
import { QuestionSummaryRow } from "../screening/QuestionsBuilder";
import { enrollCandidates, extractError, resolveStepsJobRole } from "./enrollmentApi";
import whatsappLogo from "../../assets/whatsapp.svg";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

/**
 * Preview modal — shows saved email/WhatsApp steps before enrolling candidates.
 *
 * Props:
 *   candidates – array of candidate objects from shortlist (1 or many)
 *   sequence   – selected sequence object { id, name }
 *   jobId      – job the sourcing journey is scoped to; stamped on the enrollment
 *   jobRole    – that job's title; what {{job_role}} resolves to in the copy
 *   onClose    – dismiss the whole enroll flow
 *   onBack     – return to sequence selection (re-opens AddToSequenceModal for the same candidates)
 *   onEnroll   – called after successful enrollment
 */

const PreviewModal = ({ candidates, sequence, jobId, jobRole, onClose, onBack, onEnroll }) => {
    const recruiterId = localStorage.getItem("org_admin_id");
    const orgId = localStorage.getItem("current_org_id");
    const isSingle = candidates.length === 1;

    const [previews, setPreviews] = useState([]);
    const [editedStepsByCandidate, setEditedStepsByCandidate] = useState({});
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [enrolling, setEnrolling] = useState(false);
    const [error, setError] = useState(null);
    const [formQuestions, setFormQuestions] = useState([]);
    const [formCurrency, setFormCurrency] = useState("INR");

    useEffect(() => {
        if (sequence.type !== "screening") return;
        axios
            .get(`${SOURCING_URL}/email/sequences/${sequence.id}/form`)
            .then(({ data }) => {
                if (data.form) {
                    setFormQuestions((data.form.questions || []).map(q => ({ options: [], ...q })));
                    setFormCurrency(data.form.currency || "INR");
                }
            })
            .catch(() => {}); // supplementary preview — a failure here shouldn't block enrollment
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        axios
            .post(`${SOURCING_URL}/email/enrollments/preview`, {
                candidate_ids: candidates.map(c => c.candidate_id),
                sequence_id: sequence.id,
                org_id: orgId,
                created_by: recruiterId,
            })
            .then(({ data }) => {
                const raw = data.candidates || [];
                if (raw.length === 0) throw new Error("No preview data returned.");
                // {{job_role}} is resolved here, not server-side, so do it before anything is
                // shown — the recruiter should be reviewing the copy that actually sends.
                // Both states derive from the same resolved list, or every step would look
                // edited to isStepsEdited.
                const list = raw.map(pc => ({ ...pc, steps: resolveStepsJobRole(pc.steps, jobRole) }));
                setPreviews(list);
                setEditedStepsByCandidate(
                    Object.fromEntries(list.map(pc => [pc.candidate_id, (pc.steps || []).map(s => ({ ...s }))]))
                );
                setSelectedId(list[0]?.candidate_id ?? null);
            })
            .catch(e => setError(extractError(e, "Failed to load preview.")))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const activeId = isSingle ? candidates[0]?.candidate_id : selectedId;

    const handleEmailEdit = (idx, field, value) => {
        setEditedStepsByCandidate(prev => ({
            ...prev,
            [activeId]: prev[activeId].map((s, i) =>
                i === idx ? { ...s, email: { ...s.email, [field]: value } } : s
            ),
        }));
    };

    const isStepsEdited = (id) => {
        const original = previews.find(p => p.candidate_id === id)?.steps || [];
        const edited = editedStepsByCandidate[id] || [];
        return edited.some((s, i) => s.email && (
            s.email.subject !== original[i]?.email?.subject ||
            s.email.body !== original[i]?.email?.body
        ));
    };

    const handleConnectMailbox = async () => {
        try {
            const { data } = await axios.get(`${SOURCING_URL}/auth/gmail/connect`, {
                params: { created_by: recruiterId, org_id: orgId },
            });
            redirectToOAuth(data.auth_url);
        } catch {
            setError("Failed to start Gmail connection. Please try again.");
        }
    };

    const handleEnroll = async () => {
        setEnrolling(true);
        setError(null);
        try {
            const summary = await enrollCandidates({
                candidateIds: candidates.map(c => c.candidate_id),
                sequenceId: sequence.id,
                jobId,
                jobRole,
                overrides: Object.fromEntries(
                    candidates.map(c => [
                        c.candidate_id,
                        (editedStepsByCandidate[c.candidate_id] || [])
                            .filter(s => s.email)
                            .map(s => ({
                                step_id: s.step_id,
                                subject: s.email.subject,
                                body: s.email.body,
                            })),
                    ])
                ),
            });
            onEnroll?.(summary);
            onClose();
        } catch (e) {
            setError(extractError(e, "Enrollment failed."));
        } finally {
            setEnrolling(false);
        }
    };

    const candidate = candidates[0];
    const initials = candidate?.full_name
        ?.split(" ")
        .slice(0, 2)
        .map(n => n[0])
        .join("")
        .toUpperCase();

    const fromAddress = previews[0]?.from_address;
    const sampleSteps = editedStepsByCandidate[activeId] || [];

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/50 z-[80]" onClick={onClose} />

            {/* Modal */}
            <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none">
                <div
                    className="bg-white dark:bg-[#1B1524] rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col pointer-events-auto"
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                        <div className="flex items-center gap-3 min-w-0">
                            <button
                                onClick={onBack}
                                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-colors shrink-0"
                                title="Back to outreach selection"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div className="min-w-0">
                                <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                                    Adding {candidates.length} profile{candidates.length !== 1 ? "s" : ""} to{" "}
                                    <span className="text-purple-600 dark:text-purple-300">{sequence.name}</span> {" "}outreach
                                </h2>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                    Outreach will start immediately
                                    {sequence.type === "screening" && (
                                        <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-purple-50 dark:bg-purple-500/15 text-purple-600 dark:text-purple-300 text-[10px] font-medium">
                                            Screening form attached
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            title="Close"
                            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-colors shrink-0 ml-6"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Body */}
                    <div className="flex-1 flex min-h-0 overflow-hidden">

                        {/* Left panel — recipient(s) */}
                        <div className="w-64 shrink-0 border-r border-gray-100 dark:border-gray-700/50 flex flex-col overflow-y-auto">
                            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700/50">
                                <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                                    Recipients ({candidates.length})
                                </p>
                            </div>

                            {isSingle ? (
                                <div className="px-4 py-4">
                                    <div className="flex items-start gap-3">
                                        <div
                                            className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0"
                                            style={{ background: "linear-gradient(135deg, #a78bfa, #60a5fa)" }}
                                        >
                                            {initials}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                    {candidate.full_name}
                                                </p>
                                                {candidate.linkedin_url && (
                                                    <a
                                                        href={candidate.linkedin_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={e => e.stopPropagation()}
                                                        className="text-blue-600 dark:text-blue-300 hover:text-blue-700 shrink-0"
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                                        </svg>
                                                    </a>
                                                )}
                                            </div>
                                            {(candidate.current_title || candidate.current_company) && (
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                                                    {candidate.current_title}
                                                    {candidate.current_title && candidate.current_company && " · "}
                                                    {candidate.current_company}
                                                </p>
                                            )}
                                            {candidate.location && (
                                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{candidate.location}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="px-3 py-3 flex flex-col gap-1.5">
                                    {previews.map(pc => (
                                        <button
                                            key={pc.candidate_id}
                                            onClick={() => setSelectedId(pc.candidate_id)}
                                            className={`flex flex-col gap-0.5 px-2.5 py-2 rounded-lg border text-sm text-left transition-all ${
                                                selectedId === pc.candidate_id
                                                    ? "border-purple-400 bg-purple-50"
                                                    : "border-gray-100 hover:border-gray-200"
                                            }`}
                                        >
                                            <span className="font-medium text-gray-800 dark:text-white truncate">{pc.candidate_name}</span>
                                            <span className="text-xs text-gray-400 dark:text-gray-500 truncate flex items-center gap-1.5 flex-wrap">
                                                {pc.to_address || "no email"}
                                                {pc.already_enrolled && (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-300 border border-amber-200 dark:border-amber-500/40">
                                                        already in an outreach
                                                    </span>
                                                )}
                                                {isStepsEdited(pc.candidate_id) && (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-50 dark:bg-purple-500/15 text-purple-600 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30">
                                                        edited
                                                    </span>
                                                )}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Right panel — email/whatsapp steps */}
                        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">

                            {loading && (
                                <div className="flex items-center justify-center py-20">
                                    <span className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                                </div>
                            )}

                            {!loading && error && (
                                <p className="text-sm text-red-500 dark:text-red-400 text-center py-20">{error}</p>
                            )}

                            {!loading && previews.length > 0 && !fromAddress && (
                                <div className="flex w-full max-w-2xl mx-auto items-center justify-between gap-3 px-4 py-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl shrink-0">
                                    <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                                        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                        </svg>
                                        Missing sender email. Connect an email account to start sending.
                                    </div>
                                    <button
                                        onClick={handleConnectMailbox}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white shrink-0"
                                        style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                                    >
                                        Connect mailbox
                                    </button>
                                </div>
                            )}

                            {!loading && previews.length > 0 && (
                                <>
                                    {formQuestions.length > 0 && (
                                        <div className="shrink-0">
                                            <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">Screening Form</h3>
                                            <div className="bg-purple-50/40 border border-purple-100 dark:border-purple-500/30 rounded-xl px-4 py-4">
                                                <div className="rounded-xl p-2 flex flex-col gap-2 max-h-64 overflow-y-auto bg-gray-50/40 border border-gray-100 dark:border-gray-700/50">
                                                    {formQuestions.map((q, i) => (
                                                        <div key={q.key || i} className="border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-white/5">
                                                            <QuestionSummaryRow q={q} currency={formCurrency} />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {isSingle && (
                                        /* Selected email / phone */
                                        <div className="flex items-center gap-6 shrink-0 flex-wrap">
                                            {sampleSteps.some(s => s.email) && (
                                                <div className="flex items-center gap-3">
                                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 shrink-0">
                                                        Candidate Email Address
                                                    </span>
                                                    <div className="px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-white/[0.03] text-sm text-gray-700 dark:text-gray-300">
                                                        {previews[0].to_address || <span className="text-gray-400 dark:text-gray-500 italic">No Email Found</span>}
                                                    </div>
                                                </div>
                                            )}
                                            {sampleSteps.some(s => s.whatsapp) && (
                                                <div className="flex items-center gap-3">
                                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 shrink-0">
                                                        Candidate Phone
                                                    </span>
                                                    <div className="px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-white/[0.03] text-sm text-gray-700 dark:text-gray-300">
                                                        {previews[0].phone || <span className="text-gray-400 dark:text-gray-500 italic">No Phone Found</span>}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Step cards */}
                                    {sampleSteps.map((s, idx) => (
                                        <React.Fragment key={s.step_id}>
                                            <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden shrink-0 mb-2">
                                                {/* Card header */}
                                                <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 dark:bg-white/[0.03] border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                                                    <div>
                                                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                                                            Step {s.step_number}: {s.is_call_step ? "📞 Call" : s.email && s.whatsapp ? "Email + WhatsApp" : s.whatsapp ? "WhatsApp" : "Email"}
                                                            {s.whatsapp && <img src={whatsappLogo} alt="" className="w-3.5 h-3.5" />}
                                                        </span>
                                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                                            {s.step_number === 1
                                                                ? "Sent immediately after adding the profile to the outreach."
                                                                : `Sent ${s.delay_days} day${s.delay_days !== 1 ? "s" : ""} after the previous step.`
                                                            }
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Card body */}
                                                <div className="flex flex-col">
                                                    {/* Call block */}
                                                    {s.is_call_step && (
                                                        <div className="px-5 py-4 flex flex-col gap-2 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0">
                                                            {s.call && !s.call.enabled && (
                                                                <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-xs text-red-700 dark:text-red-300">
                                                                    Call disabled — {s.call.disabled_reason}
                                                                </div>
                                                            )}
                                                            <div className="flex items-start gap-2 bg-blue-50/60 border border-blue-100 dark:border-blue-500/30 rounded-lg px-3 py-3">
                                                                <span className="text-base leading-none">☎</span>
                                                                <p className="text-xs text-blue-800 leading-snug">
                                                                    An AI voice agent calls the candidate and asks the screening questions,
                                                                    using the job context set on the form.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {/* Email block */}
                                                    {s.email && (
                                                        <div className="px-5 py-4 flex flex-col gap-3 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0">
                                                            {!s.email.enabled && (
                                                                <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-xs text-red-700 dark:text-red-300">
                                                                    Email disabled — {s.email.disabled_reason}
                                                                </div>
                                                            )}
                                                            {/* From */}
                                                            <div className="flex items-center gap-3 border-b border-gray-100 dark:border-gray-700/50 pb-3">
                                                                <span className="text-xs text-gray-400 dark:text-gray-500 w-14 shrink-0">From</span>
                                                                {fromAddress
                                                                    ? <span className="text-sm text-gray-700 dark:text-gray-300">{fromAddress}</span>
                                                                    : <span className="text-sm text-gray-400 dark:text-gray-500 italic">No email connected</span>
                                                                }
                                                            </div>

                                                            {/* Subject */}
                                                            <div className="border-b border-gray-100 dark:border-gray-700/50 pb-3">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs text-gray-400 dark:text-gray-500 w-14 shrink-0">Subject</span>
                                                                    <input
                                                                        type="text"
                                                                        value={s.email.subject}
                                                                        onChange={e => handleEmailEdit(idx, "subject", e.target.value)}
                                                                        disabled={!s.email.enabled}
                                                                        className="flex-1 text-sm text-gray-700 dark:text-gray-300 outline-none bg-transparent disabled:opacity-50"
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Body */}
                                                            <EmailBodyInput
                                                                value={s.email.body}
                                                                onChange={val => handleEmailEdit(idx, "body", val)}
                                                                placeholder="Email body"
                                                            />
                                                        </div>
                                                    )}

                                                    {/* WhatsApp block — read-only, params come from the global template */}
                                                    {s.whatsapp && (
                                                        <div className="px-5 py-4 flex flex-col gap-2">
                                                            {!s.whatsapp.enabled && (
                                                                <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-xs text-red-700 dark:text-red-300">
                                                                    WhatsApp disabled — {s.whatsapp.disabled_reason}
                                                                </div>
                                                            )}
                                                            <div className="flex items-center gap-2">
                                                                <img src={whatsappLogo} alt="" className="w-4 h-4 shrink-0" />
                                                                <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">Template</span>
                                                                <span className="text-sm text-gray-700 dark:text-gray-300">{s.whatsapp.template_name}</span>
                                                            </div>
                                                            <div className="rounded-lg px-3 py-3 flex justify-start" style={{ background: "#e5ddd5" }}>
                                                                <div
                                                                    className="max-w-[85%] rounded-lg rounded-tl-none px-3 py-2 text-sm text-gray-800 dark:text-white whitespace-pre-wrap shadow-sm"
                                                                    style={{ background: "#d9fdd3" }}
                                                                >
                                                                    {s.whatsapp.body}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Wait connector */}
                                            {idx < sampleSteps.length - 1 && (
                                                <div className="flex flex-col items-center shrink-0">
                                                    <div className="w-px h-4 bg-gray-200" />
                                                    <span className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/10 rounded-full border border-gray-200 dark:border-gray-600">
                                                        Wait {sampleSteps[idx + 1].delay_days} business day{sampleSteps[idx + 1].delay_days !== 1 ? "s" : ""}
                                                    </span>
                                                    <div className="w-px h-3 bg-gray-200" />
                                                    <svg className="w-2.5 h-2.5 text-gray-300 dark:text-gray-600" viewBox="0 0 10 10" fill="currentColor">
                                                        <polygon points="5,10 0,0 10,0" />
                                                    </svg>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Footer — same shape as the outreach picker's, so the two steps of the
                        flow put Cancel and the primary action in the same place. */}
                    <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700/50 flex items-center justify-between gap-3 shrink-0">
                        <button
                            onClick={onClose}
                            className="px-5 py-2 rounded-full text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleEnroll}
                            disabled={loading || enrolling || !!error}
                            className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium text-white disabled:opacity-40 transition-all"
                            style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                        >
                            {enrolling ? (
                                <>
                                    <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Adding...
                                </>
                            ) : (
                                <>
                                    Enroll
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PreviewModal;
